import { Dialog, Transition } from '@headlessui/react';
import { AppContext } from 'context/ContextProvider';
import { useMgmtFeeContext } from 'context/MgmtFeeContext';
import { Fragment, useContext, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { MgmtFeeTypeEnum, MgmtViewsEnum } from 'views/shared/types';
/* eslint-disable jsx-a11y/label-has-associated-control */

export default function StorageManagementFeePayment() {
  const { t } = useTranslation();
  const { formatter: currencyFormatter, gbpToTargetCurrency } = useContext(AppContext);
  const {
    state: {
      annualFeeAmount,
      forceAction,
      mgmtHashRef,
      monthlyFeeAmount,
      selectedFeeAmount,
      totalDueTodayAnnualFee,
      totalDueTodayMonthlyFee,
      viewType,
      selectedMgmtFeeType,
      accruedFee,
      hasMitCard,
      annualDiscount,
      plan,
    },
    onFeeTypeSubmit,
    onHowToPayFee,
    updateState,
  } = useMgmtFeeContext();

  const onSelectPlan = (planIn: 'annual' | 'monthly') => {
    if (planIn === 'annual') updateState({ plan: 'annual', selectedFeeAmount: totalDueTodayAnnualFee });
    if (planIn === 'monthly') updateState({ plan: 'monthly', selectedFeeAmount: totalDueTodayMonthlyFee });
  };

  const onContinue = () => {
    if (plan === 'annual') onFeeTypeSubmit(MgmtFeeTypeEnum.YEARLY);
    if (plan === 'monthly') onFeeTypeSubmit(MgmtFeeTypeEnum.MONTHLY);
  };

  const onClose = () => {
    updateState({ showModal: false, viewType: MgmtViewsEnum.NONE });
  };

  return (
    <Transition appear show as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="fixed inset-0 z-50 flex md:items-center md:justify-center bg-black/40 overflow-y-auto ">
          <div className="relative w-full max-w-4xl rounded-xl bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-900/50 overflow-y-auto">
            <div className="grid grid-cols-1 gap-6  md:grid-cols-3 relative">
              <button
                onClick={onClose}
                className="font-bold text-md absolute top-2 right-4 z-20 dark:text-gray-400 dark:hover:text-gray-200"
              >
                x
              </button>
              {/* Left */}
              <div className="md:col-span-2 pt-6 pb-6 pl-6 pr-6  md:pr-0">
                <h2 className="text-20 font-semibold text-gray-900 dark:text-white">
                  {t('account:feePolicyEnforcerTemplate.common.title')}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('account:feePolicyEnforcerTemplate.mgmtFee&AccruedFee.explainer1')}{' '}
                  {gbpToTargetCurrency.convertAndFormat(accruedFee)}
                </p>

                <div className="mt-6">
                  <h3 className="text-[14px] font-medium text-gray-900 dark:text-white">Choose a payment plan</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Well use this plan for future fees. You can change it later.
                  </p>
                </div>

                {/* Annual */}
                <label className="mt-4 block cursor-pointer rounded-lg border dark:border-slate-600 p-4 hover:border-gray-400 dark:hover:border-slate-400">
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={plan === 'annual'}
                      onChange={() => onSelectPlan('annual')}
                      className="mt-1 accent-black dark:accent-teal-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">Annually</span>
                        <span className="rounded-full bg-[#f1f5f9] dark:bg-slate-600 px-2 py-0.5 text-xs font-medium text-black dark:text-gray-200">
                          10% discount
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Pay upfront for the remainder of 2026. Your final amount may change at year-end if your
                        portfolio value changes.
                      </p>

                      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                        <div className="rounded-md bg-gray-50 dark:bg-slate-700 p-3">
                          <div className="text-gray-500 dark:text-gray-400">Covers until</div>
                          <div className="font-medium text-gray-900 dark:text-white">31 Dec 2026</div>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-slate-700 p-3">
                          <div className="text-gray-500 dark:text-gray-400">Estimated fee</div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {gbpToTargetCurrency.convertAndFormat(annualFeeAmount)}
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-slate-700 p-3">
                          <div className="text-gray-500 dark:text-gray-400">Discount</div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {gbpToTargetCurrency.convertAndFormat(annualDiscount)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-right text-sm font-medium text-gray-900 dark:text-white">
                      Due today
                      <div className="text-14">
                        {gbpToTargetCurrency.convertAndFormat(annualFeeAmount - annualDiscount)}
                      </div>
                    </div>
                  </div>
                </label>

                {/* Monthly */}
                <label className="mt-4 block cursor-pointer rounded-lg border dark:border-slate-600 p-4 hover:border-gray-400 dark:hover:border-slate-400">
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={plan === 'monthly'}
                      onChange={() => onSelectPlan('monthly')}
                      className="mt-1 accent-black dark:accent-teal-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">Monthly</div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Pay monthly in arrears using your saved card. The exact amount can vary with your portfolio
                        value.
                      </p>

                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-md bg-gray-50 dark:bg-slate-700 p-3">
                          <div className="text-gray-500 dark:text-gray-400">Estimated monthly</div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {gbpToTargetCurrency.convertAndFormat(monthlyFeeAmount)} / month
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-slate-700 p-3">
                          <div className="text-gray-500 dark:text-gray-400">First charge</div>
                          <div className="font-medium text-gray-900 dark:text-white">~3rd of next month</div>
                        </div>
                      </div>
                    </div>

                    <div className="text-right text-sm font-medium text-gray-900 dark:text-white">
                      Due today
                      <div className="text-14">{gbpToTargetCurrency.convertAndFormat(0)}</div>
                    </div>
                  </div>
                </label>
                {/* 
                <div className="mt-6 rounded-lg border bg-gray-50 p-4 text-sm text-gray-900">
                  <span className="font-medium text-[14px]">Estimates & adjustments</span>
                  <p className="mt-1">
                    Amounts may change if your portfolio value changes. We’ll show the final amount before taking
                    payment.
                  </p>
                </div> */}
              </div>

              {/* Right summary */}
              <div className="border-l dark:border-slate-700 bg-[#fbfcfd] dark:bg-slate-700">
                <div className="rounded-lg p-5">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total due today</div>
                  <div className="mt-1 text-[24px] font-semibold text-gray-900 dark:text-white">
                    {gbpToTargetCurrency.convertAndFormat(selectedFeeAmount)}
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Outstanding balance + fees due today</p>

                  <div className="flex flex-col gap-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-slate-800 p-2 border dark:border-slate-600 rounded-md mt-4">
                    <p className="font-semibold">Breakdown</p>

                    <div className="flex flex-row items-center justify-between">
                      <p>Outstanding</p>
                      <p> {gbpToTargetCurrency.convertAndFormat(accruedFee)}</p>
                    </div>

                    <div className="flex flex-row items-center justify-between">
                      <p>{plan === 'annual' ? 'Annual' : 'Monthly'} fees</p>
                      <p>
                        {plan === 'annual'
                          ? gbpToTargetCurrency.convertAndFormat(annualFeeAmount - annualDiscount)
                          : gbpToTargetCurrency.convertAndFormat(0)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Selected plan</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {plan === 'annual' ? 'Annually' : 'Monthly'}
                      </span>
                    </div>
                    {plan === 'annual' && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Benefit</span>
                        <span className="font-medium text-gray-900 dark:text-white">10% discount</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 rounded-md border dark:border-slate-600 p-3 text-sm text-gray-600 dark:text-gray-300">
                    Secure payment
                    <p className="mt-1">We’ll charge your saved card</p>
                  </div>

                  <button
                    onClick={onContinue}
                    className="mt-6 w-full rounded-lg bg-gray-900 dark:bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 dark:hover:bg-teal-600"
                  >
                    Continue
                  </button>
                  <button
                    onClick={onClose}
                    className="mt-2 w-full rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>

                  <p className="mt-4 text-xs text-gray-400">
                    Estimate only. Subject to year-end adjustment based on portfolio value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );
}
